import { Button, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_CLIENTS_CREATE_URL, API_CLIENTS_EDIT_URL } from '../../constants/apiUrls';
import { CLIENTS_EDIT_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useSnackBar } from '../../hooks/useSnackBar';
import { isAchHost } from '../../util/achAuth';
import { unwrapAchShowModel } from '../../util/achEnvelope';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import { getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';
import ClientsAdvertisersList from '../Advertisers/AdvertisersList';
import FormEndOptions from '../forms/FormEndOptions';
import ClientsUsersList from '../Users/UsersList';
import ClientsFormDefaults from './ClientsFormDefaults';
import ClientsFormGeneral from './ClientsFormGeneral';

interface DefaultValueType {
  defaultValue: string | string[];
}

interface ClientDrivenDefaultsInput {
  advertiser?: {
    traffickerId?: DefaultValueType;
  };
  contract?: {
    contractCpm?: DefaultValueType;
    pacingStrategies?: DefaultValueType;
  };
  insertionOrder?: {
    optimizationType?: DefaultValueType;
    minCtrOptimization?: DefaultValueType;
  };
  campaign?: {
    maxBid?: DefaultValueType;
  };
  targetingProfile?: {
    appBlacklistThreshold?: DefaultValueType;
    appVcrBlacklistThreshold?: DefaultValueType;
    appWhitelistTier?: DefaultValueType;
    appVcrWhitelistTier?: DefaultValueType;
  };
}

interface ClientsFormInputs {
  name: string;
  channel: string;
  reach: string;
  source: string;
  email: string;
  sharedAccessForDashboardUsers?: boolean;
  whiteLabeledReports?: boolean;
  mapsEnabled?: boolean;
  clientDrivenDefaults?: ClientDrivenDefaultsInput;
}

const defaultValues: ClientsFormInputs = {
  name: '',
  channel: '',
  reach: '',
  source: '',
  clientDrivenDefaults: {
    advertiser: {
      traffickerId: {
        defaultValue: '',
      },
    },
    contract: {
      contractCpm: {
        defaultValue: '',
      },
      pacingStrategies: {
        defaultValue: [],
      },
    },
    insertionOrder: {
      optimizationType: {
        defaultValue: '',
      },
      minCtrOptimization: {
        defaultValue: '',
      },
    },
    campaign: {
      maxBid: {
        defaultValue: '',
      },
    },
    targetingProfile: {
      appBlacklistThreshold: {
        defaultValue: '',
      },
      appVcrBlacklistThreshold: {
        defaultValue: '',
      },
      appWhitelistTier: {
        defaultValue: '',
      },
      appVcrWhitelistTier: {
        defaultValue: '',
      },
    },
  },
  email: '',
  sharedAccessForDashboardUsers: false,
  whiteLabeledReports: false,
  mapsEnabled: true,
};

const normalizePayload = (form) => {
  const returnValue = snakeCaseKeys({
    ...form,
    categorization: [form.channel, form.reach, form.source],
    channel: undefined,
    reach: undefined,
    source: undefined,
  });

  return returnValue;
};

const normalizeForm = (data) => {
  const returnValue = {
    ...camelCaseKeys(data),
    channel: data?.categorization && data?.categorization[0],
    reach: data?.categorization && data?.categorization[1],
    source: data?.categorization && data?.categorization[2],
  };

  return returnValue;
};

interface ClientsFormProps {
  id?: string;
}

function ClientsForm(props: ClientsFormProps) {
  const { id: clientId } = props;

  const isEdit = Boolean(clientId);
  const [clientSsoId, setClientSsoId] = useState<number | null>(null);

  const { apiHost, apiHostFor } = useConfig();
  const { showSnackBar } = useSnackBar();

  // Clients show opts into the per-domain flip (ACH-colk); the write paths
  // (PUT/POST in onSubmit) stay on the plain Rails apiHost — writes are out of
  // the read-surface flip scope.
  const clientsHost = apiHostFor('clients');

  const form = useForm<ClientsFormInputs>({
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    getValues,
  } = form;
  const navigate = useNavigate();

  useConfirmNavChange(() => isDirty);

  const onSubmit = async (fields) => {
    const normalizedPayload = normalizePayload(fields);
    const payload = {
      ...normalizedPayload,
      timeStamp: new Date().getTime(),
    };

    let response;
    if (isEdit) {
      response = await putRequestJSON(apiHost, generatePath(API_CLIENTS_EDIT_URL, { id: clientId }), payload);
    } else {
      response = await postRequestJSON(apiHost, API_CLIENTS_CREATE_URL, payload);
    }
    // The server can return `error` or `errors`
    let serverErrors = get(response, 'errors');
    if (!serverErrors) {
      serverErrors = get(response, 'error');
    }

    if (serverErrors) {
      let messages;
      // `errors` can be an array of strings, or an array.
      if (Array.isArray(serverErrors)) {
        messages = serverErrors;
      } else {
        messages = [serverErrors];
      }
      messages.forEach((message) => {
        showSnackBar(message, 'error', { event: 'client_save_error' });
      });
      return;
    }

    const newClientId = get(response, 'model.id');
    showSnackBar('Successfully saved', 'success', {
      event: isEdit ? 'client_updated' : 'client_created',
      client_id: isEdit ? clientId : newClientId,
    });
    reset(
      normalizeForm({
        // Do not reset form; there's a bug on the server side where the clientDrivenDefaults are not returned
        // Thus we call getValues() and override with any values from the server response.
        ...getValues(),
        ...response?.model,
      })
    );

    // If we are creating a client, then go to the edit client URL on success.
    if (!isEdit && newClientId) {
      setTimeout(() => {
        navigate(
          generatePath(CLIENTS_EDIT_URL, {
            id: newClientId,
          })
        );
      }, 1000);
    }
  };

  useEffect(() => {
    if (isEdit) {
      getRequestJSON(clientsHost, generatePath(API_CLIENTS_EDIT_URL, { id: clientId }))
        // ACH wraps the show in the D2 {model} envelope (RD-2); unwrap back to
        // the bare client this form expects — a no-op on Rails.
        .then((raw) => unwrapAchShowModel(raw, isAchHost(clientsHost)))
        .then((response) => {
          if (response.error) {
            showSnackBar(response.error, 'error', {
              event: 'client_load_error',
              client_id: clientId,
            });
            return;
          }

          if (response.id) {
            setClientSsoId(response.sso_id ?? null);
            const formValues = normalizeForm(response);
            reset(formValues);
          }
        });
    }
  }, [isEdit, clientsHost, clientId, reset, showSnackBar]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <Stack pb={9}>
        <Stack p={2} spacing={2}>
          <Stack direction="row">
            <Typography variant="h5">
              {isEdit ? 'Edit' : 'Create'} Client {isEdit && `ID ${clientId}`}
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <Stack p={2} sx={{ background: 'white', borderRadius: '5px' }}>
              <Typography variant="h6">General</Typography>
              <ClientsFormGeneral ssoId={clientSsoId} />
            </Stack>
            <Stack p={2} sx={{ background: 'white', borderRadius: '5px' }}>
              <Typography variant="h6">Defaults</Typography>
              <ClientsFormDefaults />
            </Stack>
            {clientId && (
              <Stack p={2} sx={{ background: 'white', borderRadius: '5px' }}>
                <ClientsUsersList clientId={clientId} clientSsoId={clientSsoId} />
              </Stack>
            )}
            {clientId && (
              <Stack p={2} sx={{ background: 'white', borderRadius: '5px' }}>
                <ClientsAdvertisersList clientId={clientId} clientSsoId={clientSsoId} />
              </Stack>
            )}
          </Stack>
        </Stack>
        <FormEndOptions position="fixed">
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={!isDirty}>
              Save Client
            </Button>
          </Stack>
        </FormEndOptions>
      </Stack>
    </FormProvider>
  );
}

export default ClientsForm;
